import { useCallback, useMemo } from 'react'
import { FormikHelpers } from 'formik'
import { message } from 'antd'
import { useParams } from 'react-router-dom'
import { v1 as uuid } from 'uuid'
import dayjs from 'dayjs'

import { UseQueryOptions } from '@shared/modules/use-query/types'
import { useQuery } from '@shared/modules/use-query/hooks'
import { FormOptions } from '@shared/modules/form/types'
import { AppointmentFormData } from '../types'
import { AppointmentsService } from '../services'
import { AppointmentMappers } from '../mappers'
import { localURLMaker } from '@shared/utils/url-maker'
import { AppointmentRoutes } from '../constants'
import { useBackgroundNavigate } from '@shared/hooks'

export const useAppointmentForm = ({ type = 'create' }: FormOptions) => {
  const navigate = useBackgroundNavigate()
  const params = useParams<{ id?: string; userId?: string }>()
  const baseInitialValues = useMemo<AppointmentFormData>(
    () => ({
      clientId: '',
      isInsuranced: false,
      type: 'outpatient',
      doctorId: '',
      insuranceId: '',
      scheduledAt: dayjs(),
      payment: { cash: '0', card: '0', bonus: '0', deposit: '0' },
      services: [{ uuid: uuid(), id: '', doctorId: '', discount: '0', quantity: '1', customPrice: '' }],
      servicesTotalPrice: '0',
      description: '',
    }),
    [],
  )

  const { reset, ...initialValues } = useQuery(
    useCallback(async () => {
      const result = await AppointmentsService.getOneById(params.id as string)

      if (result.status === 200) {
        const data = AppointmentMappers.formInitializer(result.data)
        return {
          ...data,
          services: !!data.services.length ? data.services : baseInitialValues.services,
        }
      } else {
        throw new Error(result.data)
      }
    }, [baseInitialValues.services, params.id]),
    useMemo<UseQueryOptions>(
      () => ({
        initialData: baseInitialValues,
        enabled: !!params.id,
      }),
      [baseInitialValues, params.id],
    ),
  )

  const onSubmit = useCallback(
    async (values: AppointmentFormData, helpers: FormikHelpers<AppointmentFormData>) => {
      helpers.setStatus(null)
      const result = await AppointmentsService.createOrUpdate(values, type)

      if (result.status === 200) {
        const { id } = result.data
        navigate(localURLMaker(AppointmentRoutes.DETAILS, { id }))

        message.success('Müştəri müvəffəqiyyətlə əlavə edildi.')
      } else if (result.status === 422) {
        helpers.setErrors(result.data)
      } else {
        message.error(result.data)
        helpers.setStatus({ type: 'error', message: result.data })
      }
    },
    [navigate, type],
  )

  return { initialValues, onSubmit }
}
